import { PROPS_, getFieldProps, getFieldPropsPath } from "basikon-common-utils"
import cloneDeep from "lodash.clonedeep"
import get from "lodash.get"
import set from "lodash/set"

// "addresses[x].addressLine" => "addressLine"
// "persons[x].invoicing.bankAccount.bankIban" => "bankIban"
export function getLeafFieldName(fieldPath) {
  const leafFieldName = fieldPath.split(".").pop()
  return leafFieldName
}

// "addresses[x].addressLine" => "addresses[x]"
// "persons[x].invoicing.bankAccount.bankIban" => "persons[x].invoicing.bankAccount"
// "persons[x].invoicing.addresses[0].bankIban" => "persons[x].invoicing.addresses[0]"
export function getNestedFieldName(fieldPath) {
  const leafFieldName = getLeafFieldName(fieldPath)
  const nestedFieldName = fieldPath.replace(`.${leafFieldName}`, "")
  return nestedFieldName
}

// "addresses[x].addressLine" => "addresses"
// "persons[x].invoicing.bankAccount.bankIban" => "persons"
// "persons[x].invoicing.addresses[0].bankIban" => "persons"
export function getRootFieldName(fieldPath) {
  const rootFieldName = fieldPath.split(".")[0]
  if (rootFieldName.endsWith("]")) return rootFieldName.split("[")[0]
  return rootFieldName
}

// "addresses[0].addressLine" => [ 'addresses', 0 ]
// "persons[0].invoicing.bankAccount.bankIban" => [ 'persons', 0 ]
// "persons[0].invoicing.addresses[0].bankIban" => [ 'persons', 0 ]
export function getRootField(fieldPath) {
  const rootFieldName = fieldPath.split(".")[0]
  if (rootFieldName.endsWith("]")) {
    const [_rootFieldName, rootArrayItemIndex] = rootFieldName.split("[")
    return [_rootFieldName, Number(rootArrayItemIndex.replace("]", ""))]
  }
  return [rootFieldName]
}

// "addresses[0].addressLine" => [ '0', 'addressLine' ]
// "persons[0].invoicing.addresses[0].bankIban" => [ '0', 'invoicing', 'addresses', '0', 'bankIban' ]
export function getSetPath(fieldPath) {
  const setPath = fieldPath.replace(/\[/g, " ").replace(/\]/g, "").replace(/\./g, " ").split(" ")
  setPath.shift() // ["addresses", "x", "addressLine"] => ["x", "addressLine"]
  return setPath
}

/**
 * Create a patch object to update a nested property within an object, preserving the previous state.
 * This function is designed to work with nested objects and arrays.
 *
 * @param {object} obj - The target object to patch.
 * @param {string|string[]} fieldPath - The path to the property to update within the object.
 * @param {any} value - The new value to set at the specified field path.
 * @returns {object} - A patch object containing the changes to apply to the original object.
 *
 * @example
 * const originalObj = { data: { value: 42 } };
 * const patch = toPatch(originalObj, 'data.value', 55);
 * // Returns: { data: { value: 55 } }
 * // Applying the patch to originalObj would result in { data: { value: 55 } }
 */
export function toPatch(obj, fieldPath, value) {
  const isFieldPathArray = Array.isArray(fieldPath)
  const fieldPaths = isFieldPathArray ? fieldPath : [fieldPath]
  const values = isFieldPathArray && !Array.isArray(value) ? [value] : value

  const patch = {}
  let copyRootArray
  let copyRootObject
  for (const [index, fieldPath] of fieldPaths.entries()) {
    const valueToSet = isFieldPathArray ? values[index] : value
    if (fieldPath.startsWith(PROPS_)) {
      patch[fieldPath] = valueToSet
      continue
    }
    const [rootFieldName, rootArrayItemIndex] = getRootField(fieldPath)
    const setPath = getSetPath(fieldPath)

    // Root field is an Array
    if (rootArrayItemIndex >= 0) {
      const rootArray = get(obj, rootFieldName, [])
      if (!copyRootArray) copyRootArray = rootArray.map((it, i) => (i === rootArrayItemIndex ? cloneDeep(it) : it)) // Clone array item to keep prevState
      set(copyRootArray, setPath, valueToSet)

      patch[rootFieldName] = copyRootArray
    }
    // Root field is an Object
    else {
      const rootObject = get(obj, rootFieldName, {})
      if (!copyRootObject) copyRootObject = cloneDeep(rootObject) // Clone object to keep prevState
      set(copyRootObject, setPath, valueToSet)

      patch[rootFieldName] = copyRootObject
    }
  }
  return patch
}

// will set deep value while mutating the pathname
// setWithMutation(x, "toto.tutu.titi", 44) => x.toto(mutated).tutu(mutated).titi(changed): 44
// test code
// let x = {
// toto: {
// tutu: { titi: 33 }, tata: { tete: 55 },
// array: [{ subArray: [{ member: 66 }] }],
// }
// }
// const field = x.toto.tutu
// const field2 = x.toto.tata
// setWithMutation(x, "toto.tutu.titi", "changed_44")
// setWithMutation(x, "toto.array[0].subArray.0", "changed_66")
// setWithMutation(x, "toto.array[0].z", "changed_88")
// console.log(
// JSON.stringify(x, null, 2),
// x.toto.tutu !== field ? "OK" : "KO", // false
// x.toto.tata === field2 ? "OK" : "KO" // true
// )
export function setWithMutation(original, fieldPath, value) {
  const fieldPaths = fieldPath.replace(/\[/g, ".").replace(/\]/g, "").split(".")
  let obj = original
  for (const [index, fieldPath] of fieldPaths.entries()) {
    if (index < fieldPaths.length - 1) {
      if (Array.isArray(obj[fieldPath])) {
        obj[fieldPath] = [...obj[fieldPath]]
      } else {
        obj[fieldPath] = { ...obj[fieldPath] }
      }
    } else {
      obj[fieldPath] = value
    }
    obj = obj[fieldPath]
  }
  return original
}

/**
 * Checks if a value is empty or not.
 * @param {any} value - The value to check for emptiness.
 * @returns {boolean} True if the value is empty, false otherwise.
 */
export function isEmpty(value) {
  return !value && value !== 0
}

export function hasFieldPropsChanged({ obj, field, mandatory, min, max, type }) {
  const fieldPropsPath = getFieldPropsPath(field)
  const _mandatory = get(obj, `${fieldPropsPath}.mandatory`)
  if (_mandatory !== mandatory) return true
  const _min = get(obj, `${fieldPropsPath}.min`)
  if (_min !== min) return true
  const _max = get(obj, `${fieldPropsPath}.max`)
  if (_max !== max) return true
  if (type) {
    const _type = get(obj, `${fieldPropsPath}.type`)
    if (_type !== type) return true
  }
  return false
}

// Sync between "props_field" and "this.props" if it is passed via component props (ex: in a layout card)
// "this.props.min" => "props_field.min"
// "this.props.max" => "props_field.max"
// "this.props.mandatory" => "props_field.mandatory"
// "this.props.type" => "props_field.type"
export function syncFieldProps({ obj, field, mandatory, prevMandatory, min, max, type, prevType }) {
  if (!obj || !field) return

  const fieldProps = getFieldProps(obj, field)

  let _mandatory
  if (mandatory && (fieldProps?.mandatory === undefined || fieldProps?.mandatory === null)) {
    _mandatory = true
  } else if ((prevMandatory ? !mandatory : mandatory === false) && fieldProps?.mandatory) {
    // If `this.props.mandatory` transitions from a truthy value to `undefined`,
    // the field's `mandatory` prop must be set to `false`.
    //
    // This is necessary when using `useOptimizedFormInput`, where fewer renders
    // mean prop changes are not implicitly corrected by subsequent re-renders.
    // The issue was not apparent in the legacy implementation because the much
    // higher render frequency effectively hid it.
    _mandatory = false
  }

  let fieldPropsPath
  if (_mandatory !== undefined) {
    fieldPropsPath = getFieldPropsPath(field)
    set(obj, `${fieldPropsPath}.mandatory`, _mandatory)
  }

  if (!isEmpty(min) && isEmpty(fieldProps?.min)) {
    if (!fieldPropsPath) fieldPropsPath = getFieldPropsPath(field)
    set(obj, `${fieldPropsPath}.min`, min)
  }

  if (!isEmpty(max) && isEmpty(fieldProps?.max)) {
    if (!fieldPropsPath) fieldPropsPath = getFieldPropsPath(field)
    set(obj, `${fieldPropsPath}.max`, max)
  }

  if (type && !fieldProps?.type) {
    if (!fieldPropsPath) fieldPropsPath = getFieldPropsPath(field)
    // Skip if the path targets an index in an array that has no elements yet,
    // so set() doesn't push a new object into an empty array.
    const [, arrayPath] = fieldPropsPath.match(/^(.*)\[\d+\]/) || []
    const array = arrayPath ? get(obj, arrayPath) : null
    if (!arrayPath || (Array.isArray(array) && array.length > 0)) {
      set(obj, `${fieldPropsPath}.type`, type)
    }
  } else if (!type && prevType && fieldProps?.type) {
    if (!fieldPropsPath) fieldPropsPath = getFieldPropsPath(field)
    set(obj, `${fieldPropsPath}.type`, null)
  }
}
